import moment, { Moment } from 'moment'
import React from 'react'

import { EuiButtonIcon, EuiDatePicker, EuiFieldText, EuiFormRow, EuiPopover } from '@elastic/eui'

export const typeableDateDisplayFormat = 'MM/DD/YYYY'
const acceptedTypedDateFormats = [typeableDateDisplayFormat, 'M/D/YYYY', 'YYYY-MM-DD']

export interface IAcTypeableDateField {
  label: string
  value: Moment | null
  onChange: (date: Moment | null) => void
  minDate?: Moment
  showRequiredError?: boolean
}

const parseTypedDate = (text: string) => moment(text.trim(), acceptedTypedDateFormats, true)

const isBeforeMinDate = (date: Moment, minDate?: Moment) => !!minDate && date.isBefore(minDate, 'day')

const formatDate = (date: Moment | null) => (date ? date.format(typeableDateDisplayFormat) : '')

export const AcTypeableDateField: React.FC<IAcTypeableDateField> = ({ label, value, onChange, minDate, showRequiredError }) => {
  const [text, setText] = React.useState<string>(formatDate(value))
  const [isCalendarOpen, setIsCalendarOpen] = React.useState<boolean>(false)

  const typedDate = parseTypedDate(text)
  const hasText = text.trim().length > 0
  const isUnparseable = hasText && !typedDate.isValid()
  const isTooEarly = hasText && typedDate.isValid() && isBeforeMinDate(typedDate, minDate)

  const isMissing = !!showRequiredError && !hasText

  const errorMessage = isMissing ? `${label} is required` : isUnparseable ? `Enter a date as ${typeableDateDisplayFormat}` : isTooEarly ? `Date must be on or after ${minDate!.format(typeableDateDisplayFormat)}` : undefined

  const onTextChange = (newText: string) => {
    setText(newText)
    const parsed = parseTypedDate(newText)
    onChange(parsed.isValid() && !isBeforeMinDate(parsed, minDate) ? parsed : null)
  }

  const onCalendarSelect = (date: Moment | null) => {
    setText(formatDate(date))
    onChange(date)
    setIsCalendarOpen(false)
  }

  const calendarButton = (
    <EuiPopover isOpen={isCalendarOpen} closePopover={() => setIsCalendarOpen(false)} anchorPosition='downRight' panelPaddingSize='none' button={<EuiButtonIcon iconType='calendar' aria-label={`Open ${label} calendar`} onClick={() => setIsCalendarOpen(!isCalendarOpen)} />}>
      <EuiDatePicker inline shadow={false} selected={value} minDate={minDate} onChange={onCalendarSelect} showTimeSelect={false} />
    </EuiPopover>
  )

  return (
    <EuiFormRow label={label} isInvalid={!!errorMessage} error={errorMessage}>
      <EuiFieldText name={label} placeholder={typeableDateDisplayFormat} value={text} onChange={e => onTextChange(e.target.value)} isInvalid={!!errorMessage} append={calendarButton} autoComplete='off' />
    </EuiFormRow>
  )
}
